import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  Trash2,
  Pencil,
  Send,
  Eye,
  Copy,
  Lock,
  Archive,
  RotateCw,
  CalendarDays,
  MessageSquare,
  Search,
  ArrowUpDown,
} from "lucide-react";
import { ShareButton } from "@/components/ShareDialog";
import { toast } from "sonner";
import { useMemo, useState } from "react";

import { DashboardShell } from "@/components/DashboardShell";
import { PARTNER_NAV } from "@/lib/nav";
import { useAuth } from "@/lib/auth";
import { SERVICE_LABEL, SERVICE_TYPES, useMyServices, type ServiceType } from "@/lib/services";
import {
  archiveListing,
  deleteListing,
  duplicateListing,
  fetchMyListings,
  LISTING_STATUS_CLASS,
  LISTING_STATUS_LABEL,
  reactivateListing,
  resubmitListing,
  submitListing,
  type DuplicateResetOptions,
  type ListingStatus,
  type PartnerListing,
} from "@/lib/listings";

const SERVICE_CHIP: Record<ServiceType, string> = {
  sale: "bg-sale/15 text-sale",
  rent: "bg-rent/15 text-rent",
  lease: "bg-lease/15 text-lease",
  parking: "bg-parking/15 text-parking",
};

const STATUS_TABS: readonly (ListingStatus | "all")[] = [
  "all",
  "draft",
  "pending",
  "published",
  "rejected",
  "archived",
];

const STATUS_TAB_LABEL: Record<ListingStatus | "all", string> = {
  all: "All",
  draft: "Draft",
  pending: "Pending",
  published: "Approved",
  rejected: "Rejected",
  archived: "Inactive",
};

const STATUS_ORDER: Record<ListingStatus, number> = {
  pending: 0,
  rejected: 1,
  draft: 2,
  published: 3,
  archived: 4,
};

type SortKey = "date_desc" | "date_asc" | "views_desc" | "enquiries_desc" | "status";

const SORT_LABEL: Record<SortKey, string> = {
  date_desc: "Newest first",
  date_asc: "Oldest first",
  views_desc: "Most viewed",
  enquiries_desc: "Most enquiries",
  status: "Status priority",
};

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });

export const Route = createFileRoute("/_authenticated/partner/listings/")({
  head: () => ({
    meta: [
      { title: "My listings · Landoo" },
      {
        name: "description",
        content: "Manage Sale, Rent, Lease and Parking listings, drafts and approval status.",
      },
    ],
  }),
  component: ListingsPage,
});

function ListingsPage() {
  const { user } = useAuth();
  const { activeServices, isLoading: svcLoading } = useMyServices();
  const qc = useQueryClient();
  const nav = useNavigate();
  const [service, setService] = useState<ServiceType | "all">("all");
  const [status, setStatus] = useState<ListingStatus | "all">("all");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState<SortKey>("date_desc");
  const [dup, setDup] = useState<PartnerListing | null>(null);

  const { data: listings = [], isLoading } = useQuery({
    queryKey: ["partner", "listings", user?.id, activeServices.join(",")],
    queryFn: () => fetchMyListings(user!.id, activeServices),
    enabled: !!user && activeServices.length > 0,
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ["partner", "listings"] });

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return listings.filter((l) => {
      if (service !== "all" && l.service !== service) return false;
      if (status !== "all" && l.status !== status) return false;
      if (!term) return true;
      return (
        l.title.toLowerCase().includes(term) ||
        l.location.toLowerCase().includes(term) ||
        (l.listingType ?? "").toLowerCase().includes(term)
      );
    });
  }, [listings, service, status, q]);

  const visible = useMemo(() => {
    const arr = [...filtered];
    switch (sort) {
      case "date_asc":
        arr.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
        break;
      case "views_desc":
        arr.sort((a, b) => b.viewCount - a.viewCount || b.createdAt.localeCompare(a.createdAt));
        break;
      case "enquiries_desc":
        arr.sort(
          (a, b) => b.enquiryCount - a.enquiryCount || b.createdAt.localeCompare(a.createdAt),
        );
        break;
      case "status":
        arr.sort(
          (a, b) =>
            STATUS_ORDER[a.status] - STATUS_ORDER[b.status] ||
            b.createdAt.localeCompare(a.createdAt),
        );
        break;
      default:
        arr.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    }
    return arr;
  }, [filtered, sort]);

  // Counts reflect the current text search + service filter, so the status
  // tabs stay in sync with what is actually visible.
  const counts = useMemo(() => {
    const scoped = listings.filter((l) => {
      if (service !== "all" && l.service !== service) return false;
      const term = q.trim().toLowerCase();
      if (!term) return true;
      return (
        l.title.toLowerCase().includes(term) ||
        l.location.toLowerCase().includes(term) ||
        (l.listingType ?? "").toLowerCase().includes(term)
      );
    });
    return STATUS_TABS.reduce<Record<string, number>>((acc, s) => {
      acc[s] = s === "all" ? scoped.length : scoped.filter((l) => l.status === s).length;
      return acc;
    }, {});
  }, [listings, service, q]);

  const confirmDuplicate = async (opts: DuplicateResetOptions) => {
    if (!dup) return;
    try {
      const newId = await duplicateListing(dup.service, dup.id, opts);
      toast.success("Duplicated as draft");
      setDup(null);
      refresh();
      nav({
        to: "/partner/listings/$service/$listingId",
        params: { service: dup.service, listingId: newId },
      });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not duplicate");
    }
  };

  return (
    <DashboardShell title="My listings" role="Partner" items={PARTNER_NAV}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-1.5">
          {(["all", ...SERVICE_TYPES] as const).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setService(s)}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold ${
                service === s ? "bg-brown text-primary-foreground" : "bg-secondary text-brown-dark"
              }`}
            >
              {s === "all" ? "All" : SERVICE_LABEL[s]}
            </button>
          ))}
        </div>
        <Link
          to="/partner/listings/new"
          className="inline-flex items-center gap-1.5 rounded-full bg-brand px-4 py-2 text-xs font-semibold text-brown-dark shadow-soft"
        >
          <Plus className="h-4 w-4" /> New listing
        </Link>
      </div>

      <div className="mb-3 grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto]">
        <div className="relative min-w-0">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by title, type or location…"
            className="w-full rounded-full border border-border bg-card py-2 pl-9 pr-3 text-xs outline-none focus:border-brand"
          />
        </div>
        <label className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-2 text-xs font-semibold text-brown-dark">
          <ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-muted-foreground">Sort</span>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value as SortKey)}
            className="bg-transparent outline-none"
          >
            {Object.entries(SORT_LABEL).map(([v, l]) => (
              <option key={v} value={v}>
                {l}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="mb-4 flex flex-wrap gap-1.5">
        {STATUS_TABS.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setStatus(s)}
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold ${
              status === s
                ? "bg-brown-dark text-primary-foreground"
                : "border border-border bg-card text-brown-dark"
            }`}
          >
            {STATUS_TAB_LABEL[s]}
            <span
              className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold ${
                status === s ? "bg-white/20" : "bg-secondary text-brown-dark"
              }`}
            >
              {counts[s] ?? 0}
            </span>
          </button>
        ))}
      </div>

      {!svcLoading && activeServices.length === 0 && (
        <div className="rounded-2xl border border-dashed border-border bg-card p-6 text-sm text-muted-foreground">
          This service is not activated.
          <div className="mt-3">
            <Link
              to="/partner/services"
              className="inline-flex rounded-full bg-brand px-4 py-2 text-xs font-semibold text-brown-dark"
            >
              Activate a service
            </Link>
          </div>
        </div>
      )}

      {isLoading && activeServices.length > 0 && (
        <div className="rounded-2xl border border-border bg-card p-6 text-sm text-muted-foreground">
          Loading listings…
        </div>
      )}

      {!isLoading && activeServices.length > 0 && visible.length === 0 && (
        <div className="rounded-2xl border border-dashed border-border bg-card p-6 text-sm text-muted-foreground">
          No listings match these filters.
        </div>
      )}

      <div className="space-y-2">
        {visible.map((l) => {
          const underReview = l.status === "pending";
          return (
            <div
              key={`${l.service}-${l.id}`}
              className="rounded-2xl border border-border bg-card p-4 shadow-soft"
            >
              <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-3 sm:flex sm:flex-wrap sm:justify-between">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span
                      className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${SERVICE_CHIP[l.service]}`}
                    >
                      {SERVICE_LABEL[l.service]}
                    </span>
                    <span
                      className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${LISTING_STATUS_CLASS[l.status]}`}
                    >
                      {LISTING_STATUS_LABEL[l.status]}
                    </span>
                    {underReview && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-2 py-0.5 text-[10px] font-semibold text-brown-dark">
                        <Lock className="h-3 w-3" /> Under Admin Review
                      </span>
                    )}
                  </div>
                  <h3 className="mt-1 truncate text-sm font-semibold text-brown-dark">{l.title}</h3>
                  <p className="text-xs text-muted-foreground">
                    {l.listingType ? `${l.listingType} · ` : ""}
                    {l.location}
                  </p>
                  <p className="text-xs font-semibold text-brown-dark">{l.priceLabel}</p>

                  <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
                    <span className="inline-flex items-center gap-1">
                      <Eye className="h-3 w-3" /> {l.viewCount} views
                    </span>
                    <Link
                      to="/partner/listings/$service/$listingId/enquiries"
                      params={{ service: l.service, listingId: l.id }}
                      className="inline-flex items-center gap-1 rounded-full bg-secondary px-2 py-0.5 font-semibold text-brown-dark hover:brightness-95"
                    >
                      <MessageSquare className="h-3 w-3" /> {l.enquiryCount} enquiries
                    </Link>
                    <span className="inline-flex items-center gap-1">
                      <CalendarDays className="h-3 w-3" /> Listed {fmtDate(l.createdAt)}
                    </span>
                  </div>

                  {l.status === "rejected" && l.moderationNotes && (
                    <p className="mt-1 text-[11px] text-coral">Admin note: {l.moderationNotes}</p>
                  )}
                </div>

                <div className="flex flex-wrap justify-end gap-1.5">
                  {l.status === "published" && (
                    <Link
                      to="/property/$id"
                      params={{ id: l.id }}
                      className="inline-flex items-center gap-1 rounded-full bg-secondary px-3 py-1.5 text-xs font-semibold text-brown-dark"
                    >
                      <Eye className="h-3.5 w-3.5" /> View
                    </Link>
                  )}

                  {l.status === "published" && (
                    <ShareButton listing={{ id: l.id, title: l.title }} />
                  )}

                  <button
                    type="button"
                    disabled={underReview}
                    onClick={() =>
                      nav({
                        to: "/partner/listings/$service/$listingId",
                        params: { service: l.service, listingId: l.id },
                      })
                    }
                    className="inline-flex items-center gap-1 rounded-full bg-secondary px-3 py-1.5 text-xs font-semibold text-brown-dark disabled:cursor-not-allowed disabled:opacity-50"
                    title={underReview ? "Editing is locked while under admin review" : "Edit"}
                  >
                    <Pencil className="h-3.5 w-3.5" /> Edit
                  </button>

                  <button
                    type="button"
                    onClick={() => setDup(l)}
                    className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-xs font-semibold text-brown-dark"
                  >
                    <Copy className="h-3.5 w-3.5" /> Duplicate
                  </button>

                  {l.status === "rejected" && (
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          await resubmitListing(l.service, l.id);
                          toast.success("Resubmitted for approval");
                          refresh();
                        } catch (e) {
                          toast.error(e instanceof Error ? e.message : "Could not resubmit");
                        }
                      }}
                      className="inline-flex items-center gap-1 rounded-full bg-coral px-3 py-1.5 text-xs font-semibold text-white"
                    >
                      <RotateCw className="h-3.5 w-3.5" /> Resubmit
                    </button>
                  )}

                  {l.status === "draft" && (
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          await submitListing(l.service, l.id);
                          toast.success("Submitted for approval");
                          refresh();
                        } catch (e) {
                          toast.error(e instanceof Error ? e.message : "Could not submit");
                        }
                      }}
                      className="inline-flex items-center gap-1 rounded-full bg-coral px-3 py-1.5 text-xs font-semibold text-white"
                    >
                      <Send className="h-3.5 w-3.5" /> Submit
                    </button>
                  )}

                  {l.status === "published" && (
                    <button
                      type="button"
                      onClick={async () => {
                        if (
                          !confirm(
                            "Mark this listing as inactive? It will be removed from the map.",
                          )
                        )
                          return;
                        try {
                          await archiveListing(l.service, l.id);
                          toast.success("Listing set to inactive");
                          refresh();
                        } catch (e) {
                          toast.error(e instanceof Error ? e.message : "Could not update");
                        }
                      }}
                      className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-xs font-semibold text-brown-dark"
                    >
                      <Archive className="h-3.5 w-3.5" /> Deactivate
                    </button>
                  )}

                  {l.status === "archived" && (
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          await reactivateListing(l.service, l.id);
                          toast.success("Sent for approval");
                          refresh();
                        } catch (e) {
                          toast.error(e instanceof Error ? e.message : "Could not reactivate");
                        }
                      }}
                      className="inline-flex items-center gap-1 rounded-full bg-coral px-3 py-1.5 text-xs font-semibold text-white"
                    >
                      <RotateCw className="h-3.5 w-3.5" /> Reactivate
                    </button>
                  )}

                  {l.status === "draft" && (
                    <button
                      type="button"
                      onClick={async () => {
                        if (!confirm("Delete this draft?")) return;
                        try {
                          await deleteListing(l.service, l.id);
                          toast.success("Draft deleted");
                          refresh();
                        } catch (e) {
                          toast.error(e instanceof Error ? e.message : "Could not delete");
                        }
                      }}
                      className="inline-flex items-center gap-1 rounded-full border border-coral/40 px-3 py-1.5 text-xs font-semibold text-coral"
                    >
                      <Trash2 className="h-3.5 w-3.5" /> Delete
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {dup && (
        <DuplicateDialog listing={dup} onCancel={() => setDup(null)} onConfirm={confirmDuplicate} />
      )}
    </DashboardShell>
  );
}

function DuplicateDialog({
  listing,
  onCancel,
  onConfirm,
}: {
  listing: PartnerListing;
  onCancel: () => void;
  onConfirm: (opts: DuplicateResetOptions) => void | Promise<void>;
}) {
  const [opts, setOpts] = useState<DuplicateResetOptions>({
    resetImages: false,
    resetModerationNotes: true,
    resetAvailableFrom: true,
  });
  const [busy, setBusy] = useState(false);
  const toggle = (k: keyof DuplicateResetOptions) => setOpts((o) => ({ ...o, [k]: !o[k] }));

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-3 sm:items-center">
      <div className="w-full max-w-md rounded-2xl bg-card p-5 shadow-lg">
        <h3 className="text-sm font-bold text-brown-dark">Duplicate as new draft</h3>
        <p className="mt-1 text-xs text-muted-foreground">
          A draft copy of “{listing.title}” will be created. Choose which fields to reset before
          saving.
        </p>
        <div className="mt-3 space-y-2">
          <ResetRow
            label="Reset photos"
            hint="Start with an empty photo gallery."
            checked={opts.resetImages}
            onChange={() => toggle("resetImages")}
          />
          <ResetRow
            label="Clear admin notes"
            hint="Remove moderation feedback from the previous review."
            checked={opts.resetModerationNotes}
            onChange={() => toggle("resetModerationNotes")}
          />
          <ResetRow
            label="Clear dates"
            hint="Wipe availability date so you can pick a fresh one."
            checked={opts.resetAvailableFrom}
            onChange={() => toggle("resetAvailableFrom")}
          />
        </div>
        <div className="mt-4 flex gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 rounded-xl border border-border py-2 text-xs font-semibold text-brown-dark"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await onConfirm(opts);
              } finally {
                setBusy(false);
              }
            }}
            className="flex-1 rounded-xl bg-coral py-2 text-xs font-semibold text-white shadow-soft disabled:opacity-60"
          >
            {busy ? "Creating…" : "Create draft"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ResetRow({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint: string;
  checked: boolean;
  onChange: () => void;
}) {
  return (
    <label className="flex items-start gap-3 rounded-xl border border-border p-3">
      <input type="checkbox" className="mt-1 h-4 w-4" checked={checked} onChange={onChange} />
      <div className="min-w-0">
        <div className="text-xs font-semibold text-brown-dark">{label}</div>
        <p className="text-[11px] text-muted-foreground">{hint}</p>
      </div>
    </label>
  );
}
